/**
 * How long a subscription plan lasts, said in words and in dates.
 *
 * The form used to offer Months, Years and Custom while the backend accepted
 * minutes, hours and days as well -- so the short plans could not be created at
 * all, and the one that was created through a custom window looked like it ran
 * all day, because the list printed `toLocaleDateString()` and dropped the
 * times entirely.
 *
 * The arithmetic here mirrors SubscriptionPlan.DURATION_UNIT_DELTAS exactly:
 *
 *     months -> 30 days      years -> 365 days
 *
 * Those are NOT calendar months and years. A plan bought on 31 January ends on
 * 2 March. That is the server's existing behaviour and this file deliberately
 * copies it rather than improving on it: a form that predicts a different
 * expiry from the one the customer will get is worse than a form that predicts
 * nothing, which is what we had.
 */

/** The units, in the order the form offers them. */
export const DURATION_UNITS = [
  { value: 'minutes', label: 'Minutes', group: 'Short run' },
  { value: 'hours', label: 'Hours', group: 'Short run' },
  { value: 'days', label: 'Days', group: 'Short run' },
  { value: 'months', label: 'Months', group: 'Recurring' },
  { value: 'years', label: 'Years', group: 'Recurring' },
  { value: 'custom', label: 'Custom dates', group: 'Fixed window' },
];

/** Milliseconds per unit. Matches the backend's deltas, 30/365 included. */
export const DURATION_UNIT_MS = {
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  months: 30 * 24 * 60 * 60 * 1000,
  years: 365 * 24 * 60 * 60 * 1000,
};

const SINGULAR = {
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
  months: 'month',
  years: 'year',
};

/**
 * "10 minutes", "1 month" -- never "1 months".
 *
 * Both the plan list and the purchase page printed `${value} ${type}` raw,
 * which was merely untidy at 12 and wrong at 1. Single-unit plans are now
 * possible, so it stopped being cosmetic.
 */
export function planDurationLabel(plan) {
  if (!plan) return '';
  const type = plan.duration_type;
  if (type === 'custom') return 'custom window';

  const value = Number(plan.duration_value);
  if (!Number.isFinite(value) || value <= 0) return '';

  const singular = SINGULAR[type];
  if (!singular) return `${value} ${type}`;
  return `${value} ${value === 1 ? singular : `${singular}s`}`;
}

/** The same thing without the number: "per month" rather than "per 1 month". */
export function planPerLabel(plan) {
  if (!plan) return '';
  if (plan.duration_type === 'custom') return 'custom window';
  const value = Number(plan.duration_value);
  const singular = SINGULAR[plan.duration_type];
  if (!singular) return planDurationLabel(plan);
  return value === 1 ? singular : `${value} ${singular}s`;
}

/** How long the plan runs, in milliseconds, or null when it cannot be known. */
export function planDurationMs(plan) {
  if (!plan) return null;
  if (plan.duration_type === 'custom') {
    const start = new Date(plan.custom_start_date);
    const end = new Date(plan.custom_end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const span = end.getTime() - start.getTime();
    return span > 0 ? span : null;
  }
  const per = DURATION_UNIT_MS[plan.duration_type];
  const value = Number(plan.duration_value);
  if (!per || !Number.isFinite(value) || value <= 0) return null;
  return per * value;
}

/**
 * When someone subscribing at `from` would expire.
 *
 * `from` is a parameter rather than `new Date()` so this is testable, and so a
 * custom window answers with its own end date instead of an offset from now.
 */
export function planExpiryFrom(plan, from = new Date()) {
  if (!plan) return null;
  if (plan.duration_type === 'custom') {
    const end = new Date(plan.custom_end_date);
    return Number.isNaN(end.getTime()) ? null : end;
  }
  const span = planDurationMs(plan);
  return span === null ? null : new Date(from.getTime() + span);
}

/** A date with its time, because a three-minute window is not a date. */
export function formatDateTime(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The custom window, both ends, times included.
 *
 * The list showed `25/08/2026 - 25/08/2026` for a window that ran 16:16 to
 * 16:19 -- identical to one that ran all day, which is exactly how it was read.
 * When both ends fall on one day the date is not repeated.
 */
export function formatCustomWindow(plan) {
  const start = plan?.custom_start_date ? new Date(plan.custom_start_date) : null;
  const end = plan?.custom_end_date ? new Date(plan.custom_end_date) : null;
  const startOk = start && !Number.isNaN(start.getTime());
  const endOk = end && !Number.isNaN(end.getTime());

  if (!startOk && !endOk) return 'N/A';
  if (!startOk) return `until ${formatDateTime(end)}`;
  if (!endOk) return `from ${formatDateTime(start)}`;

  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    const time = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${formatDateTime(start)} → ${time}`;
  }
  return `${formatDateTime(start)} → ${formatDateTime(end)}`;
}

/** How the list should describe a plan's duration, whatever its type. */
export function planDurationSummary(plan) {
  if (!plan) return '';
  return plan.duration_type === 'custom'
    ? formatCustomWindow(plan)
    : planDurationLabel(plan);
}

/**
 * Whether a custom window is already over.
 *
 * A plan whose window closed is still offered for sale, which is how a
 * subscription that had already expired came to be sold. The form cannot fix
 * that on its own, but it can refuse to create one.
 */
export function isCustomWindowPast(plan, now = new Date()) {
  if (plan?.duration_type !== 'custom') return false;
  const end = new Date(plan.custom_end_date);
  return !Number.isNaN(end.getTime()) && end.getTime() <= now.getTime();
}
